'use client'

import { useT, type TranslateFn, useFormat } from '@/lib/i18n'

/**
 * Stage 41 — the historical records behind one evidence number, as a table.
 *
 * WHY IT EXISTS
 * The rows used to be one long sentence per record — date · side · shipments · wagon,
 * then cargo · ЕТСНГ · route · stations · weight · interval on a second line. Everything
 * was there, but nothing was scannable: fifty of those lines read as a paragraph, and the
 * cargo code — the field the count above is defined by — was buried mid-sentence with no
 * column of its own and no ГНГ at all.
 *
 * DESIGN LANGUAGE
 * Borrowed from «Похожие кейсы в периоде» on `/cases/{id}` (the AG Grid panel): bordered
 * container, a summary bar above the grid, a quiet uppercase header, 1-line dense rows,
 * hairline separators, identifiers in mono/tabular figures, and a muted second line inside
 * a cell for the value that qualifies the first. Only the LOOK is shared — that page's
 * grid, its columns, its business logic and its data stay where they are.
 *
 * WHAT IT DOES NOT DO
 * It computes nothing, sorts nothing and fetches nothing. It receives finished cells from
 * `toExampleCells` in the server's order, and prints them. A field the payload does not
 * carry renders as «—» or as an omitted line — never as «ЕТСНГ undefined».
 */
import { hasExactIdentity, hasExactLegIdentity } from '../cross-unit/cross-unit.evidence-examples'
import type { ExampleCells, VariantGroup } from '../cross-unit/cross-unit.evidence-examples'

/* ── shell ───────────────────────────────────────────────────────────────────── */

/**
 * Bordered container + summary bar, shared by every dataset type.
 *
 * The bar always names the FULL set: a capped sample must never read as the whole history.
 */
export function EvidenceTableShell({
  summary,
  note,
  children,
}: {
  /** Short eyebrow — «Показано 50 из 644». */
  summary: string
  /** The existing full-sentence footnote + selection description, unchanged. */
  note?: string | null
  children: React.ReactNode
}) {
  return (
    <div
      data-block="evidence-history"
      className="overflow-hidden rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-surface)]"
    >
      <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-2.5 py-1.5">
        <p className="text-[10px] font-medium uppercase tracking-[0.06em] text-[var(--text-tertiary)]">{summary}</p>
      </div>
      {/* Horizontal scroll is contained here and never reaches the page or the drawer. */}
      <div className="overflow-x-auto">{children}</div>
      {note && (
        <p className="border-t border-[var(--border-subtle)] px-2.5 py-1.5 text-[10px] leading-relaxed text-[var(--text-tertiary)]">
          {note}
        </p>
      )}
    </div>
  )
}

/* ── cells ───────────────────────────────────────────────────────────────────── */

const TH = 'border-b border-[var(--border-subtle)] px-1.5 py-1 text-left align-bottom font-medium ' +
  'uppercase tracking-[0.04em] text-[10px] leading-tight text-[var(--text-tertiary)]'
const TD = 'border-b border-[var(--border-subtle)] px-1.5 py-1 align-top text-[11px] leading-tight'
const MUTED = 'text-[10px] leading-tight text-[var(--text-tertiary)]'
const DASH = <span className="text-[var(--text-tertiary)]">—</span>

/**
 * Primary line + optional muted qualifier — the pattern the reference grid uses for cargo.
 *
 * `truncate` on both lines is what keeps a long value INSIDE its column: a nowrap cell
 * that is one character too wide would otherwise paint over the neighbouring column, and
 * two numbers on top of each other are worse than one number cut short with a tooltip.
 */
function Stacked({ main, sub, mono, title, end }: {
  main: string | null; sub?: string | null; mono?: boolean; title?: string
  /** Right-aligned column (numbers). */
  end?: boolean
}) {
  if (!main && !sub) return DASH
  const face = mono ? 'font-mono tabular-nums' : ''
  return (
    <div
      className={`flex min-w-0 flex-col ${end ? 'items-end' : ''}`}
      title={title ?? [main, sub].filter(Boolean).join(' · ')}
    >
      {main && <span className={`truncate ${face} text-[var(--text-primary)]`}>{main}</span>}
      {sub && <span className={`truncate ${face} ${MUTED}`}>{sub}</span>}
    </div>
  )
}

/**
 * «Код груза» — the column this stage exists for.
 *
 * Each classifier gets its own line so the code can be read and copied on its own. A code
 * the payload does not carry simply has no line; when neither is present the cell is «—».
 */
function CargoCodeCell({ etsng, gng }: { etsng: string | null; gng: string | null }) {
  if (!etsng && !gng) return DASH
  return (
    <div className="flex min-w-0 flex-col font-mono tabular-nums">
      {etsng && <span className="truncate text-[var(--text-primary)]">ЕТСНГ {etsng}</span>}
      {gng && <span className={`truncate ${MUTED}`}>ГНГ {gng}</span>}
    </div>
  )
}

/** Cargo name: two lines at most, full text on hover. */
function CargoCell({ name }: { name: string | null }) {
  if (!name) return DASH
  return (
    <span className="line-clamp-2 break-words text-[var(--text-primary)]" title={name}>
      {name}
    </span>
  )
}

/**
 * Груз ОДНОЙ ноги пары: название, а под ним коды этой же ноги.
 *
 * Коды стоят внутри своего груза, а не отдельной колонкой на всю строку: пара несёт
 * два ЕТСНГ и два ГНГ, и общая колонка кодов заставляла бы читателя гадать, к какой
 * стороне относится код. Отсутствующее значение — «—» или пропущенная строка; ни одно
 * поле не добирается со второй ноги.
 */
function CargoLegCell({ name, etsng, gng }: {
  name: string | null; etsng: string | null; gng: string | null
}) {
  if (!name && !etsng && !gng) return DASH
  const title = [name, etsng ? `ЕТСНГ ${etsng}` : null, gng ? `ГНГ ${gng}` : null]
    .filter(Boolean).join(' · ')
  return (
    <div className="flex min-w-0 flex-col" title={title}>
      {name
        ? <span className="line-clamp-2 break-words text-[var(--text-primary)]">{name}</span>
        : <span className={MUTED}>—</span>}
      {/* Каждый классификатор — своей строкой: в одну строку «ЕТСНГ 122022 · ГНГ
          44101130» не помещается в колонку и обрезается ровно на коде, ради
          которого таблицу и читают. */}
      {etsng && <span className={`truncate font-mono tabular-nums ${MUTED}`}>ЕТСНГ {etsng}</span>}
      {gng && <span className={`truncate font-mono tabular-nums ${MUTED}`}>ГНГ {gng}</span>}
    </div>
  )
}

/**
 * Обе стороны одного парного показателя — плательщика или массы.
 *
 * Каждая нога подписана и стоит на своей строке; пустая сторона печатается как «—»
 * и никогда не заимствует значение соседней. «0» приходит из источника как есть —
 * это не пустое значение, и подменять его на «—» здесь нечем.
 */
function LegPairCell({ imp, exp, mono = true, end = false, unit }: {
  imp: string | null; exp: string | null; mono?: boolean; end?: boolean
  /** Единица измерения для подсказки — в самой ячейке её несёт заголовок колонки. */
  unit?: string
}) {
  const t = useT()
  if (!imp && !exp) return DASH
  const face = mono ? 'font-mono tabular-nums' : ''
  const suffix = unit ? ` ${unit}` : ''
  const line = (label: string, v: string | null) => (
    <span className={`truncate text-[10px] ${v ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}>
      <span className="font-sans text-[9px] text-[var(--text-tertiary)]">{label} </span>{v ?? '—'}
    </span>
  )
  return (
    <div
      className={`flex min-w-0 flex-col ${face} ${end ? 'items-end' : ''}`}
      title={`${t('analytics.evi.impLeg')} ${imp ?? '—'}${suffix} → ${t('analytics.evi.expLeg')} ${exp ?? '—'}${suffix}`}
    >
      {line(t('analytics.evi.impShort'), imp)}
      {line(t('analytics.evi.expShort'), exp)}
    </div>
  )
}

/** Countries first, the KZ stations under them — a corridor is not a pair of codes. */
function RouteCell({ route, stations }: { route: string | null; stations: string | null }) {
  return <Stacked main={route} sub={stations} title={[route, stations].filter(Boolean).join(' · ') || undefined} />
}

/** Both legs of a pair, each labelled; a leg row shows the single shipment it is. */
function ShipmentsCell({ r }: { r: ExampleCells }) {
  const t = useT()
  if (r.side === 'pair') {
    if (!r.importShipment && !r.exportShipment) return DASH
    // 10px вместо 11px: восьмизначный номер с подписью «имп»/«эксп» иначе не
    // помещается в колонку и обрезается — а по номеру отправки строку ищут.
    const leg = (label: string, id: string) => (
      <span className="truncate text-[10px] text-[var(--text-primary)]">
        <span className="font-sans text-[9px] text-[var(--text-tertiary)]">{label} </span>{id}
      </span>
    )
    return (
      <div
        className="flex min-w-0 flex-col font-mono tabular-nums"
        title={t('analytics.hist.exportArrow', { imp: r.importShipment ?? '—', exp: r.exportShipment ?? '—' })}
      >
        {r.importShipment && leg(t('analytics.hist.impShort'), r.importShipment)}
        {r.exportShipment && leg(t('analytics.hist.expShort'), r.exportShipment)}
      </div>
    )
  }
  return r.shipment
    ? <span className="block truncate font-mono tabular-nums text-[var(--text-primary)]">{r.shipment}</span>
    : DASH
}

function UnitCell({ r }: { r: ExampleCells }) {
  const t = useT()
  if (!r.unit) return DASH
  return <Stacked main={r.unit} sub={r.unitKind === 'container' ? t('analytics.evi.container') : null} mono />
}

/* ── the movements table ─────────────────────────────────────────────────────── */

/**
 * Column set follows the shape of the rows the server sent, not a guess from the dataset
 * name: pair-shaped rows (scenarios 1–5) carry two shipments and an interval; leg-shaped
 * rows (scenarios 6–7) carry one shipment and a side, and have no interval to show.
 */
export function EvidenceExamplesTable({ rows, startIndex = 0, onOpenRow }: {
  rows: ExampleCells[]
  startIndex?: number
  /**
   * Открыть подробности строки. Не передан — таблица остаётся как была: ни курсора,
   * ни фокуса, ни обработчиков. Кликабельны только строки-пары с ТОЧНОЙ идентичностью
   * обеих ног: открывать подробности «примерно этой» перевозки нельзя.
   */
  onOpenRow?: (row: ExampleCells) => void
}) {
  const t = useT()
  const isPair = rows.some((r) => r.side === 'pair')
  return isPair
    ? <PairExamplesTable rows={rows} startIndex={startIndex} onOpenRow={onOpenRow} />
    : <LegExamplesTable rows={rows} startIndex={startIndex} onOpenRow={onOpenRow} />
}

/**
 * Поведение кликабельной строки: мышь, Enter, Space и фокус с клавиатуры.
 *
 * Space гасится на `keydown`, иначе браузер прокрутит контейнер под карточкой.
 * Клик по вложенному интерактивному элементу до строки не доходит — строка
 * проверяет, что событие пришло не из кнопки/ссылки/поля.
 */
export function rowInteraction(
  row: ExampleCells,
  onOpenRow: (r: ExampleCells) => void,
  // t обязателен: подпись строки — это её aria-label, и пустой глагол сделал бы
  // строку безымянной для скринридера. Тесты передают переводчик из CATALOG.
  t: TranslateFn,
) {
  // Проверка идёт по наличию `closest`, а не по `instanceof Element`: обработчик
  // должен работать одинаково в браузере и там, где глобального `Element` нет.
  //
  // Сама строка тоже размечена как `role="button"`, поэтому найденный предок
  // сравнивается со строкой: без этого `closest` находил бы саму строку и гасил
  // КАЖДЫЙ клик по ней.
  const fromInteractive = (t: unknown, row_: unknown): boolean => {
    const el = t as { closest?: (s: string) => unknown } | null
    if (typeof el?.closest !== 'function') return false
    const hit = el.closest('button, a, input, select, textarea, [role="button"]')
    return !!hit && hit !== row_
  }
  const label = [
    t('analytics.evi.openDetail'),
    [row.importShipment, row.exportShipment, row.shipment].filter(Boolean).join(' → '),
  ].filter(Boolean).join(' ')
  return {
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': label,
    'data-row-clickable': 'true',
    onClick: (e: React.MouseEvent) => {
      if (fromInteractive(e.target, e.currentTarget)) return
      onOpenRow(row)
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (fromInteractive(e.target, e.currentTarget)) return
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault()
        onOpenRow(row)
      }
    },
  }
}

/**
 * Строка-пара: «ИМПОРТНАЯ НОГА → ЭКСПОРТНАЯ НОГА» (сценарии 1–5).
 *
 * Каждое поле, которого у пары ДВА, показывается двумя значениями: груз со своими
 * кодами, плательщик, масса. Прежняя раскладка несла по одной колонке «Груз», «Код
 * груза» и «Вес», и в них стояла экспортная сторона — историю вида «Плиты
 * древесностружечные → Вагоны железнодорожные» читатель видел как перевозку вагонов,
 * а импортной ноги не видел вовсе. Отдельная колонка кодов при этом исчезла: коды
 * переехали внутрь своего груза, где к ним нельзя приписать чужую сторону.
 *
 * Ширины считаны по самым длинным РЕАЛЬНЫМ значениям (10-значная дата, 8-значный номер
 * отправки с подписью, «ЕТСНГ 141092 · ГНГ 99220000», пара стран); сумма укладывается в
 * 940-пиксельный drawer, а на узком экране контейнер прокручивается сам и страница —
 * никогда.
 */
function PairExamplesTable({ rows, startIndex, onOpenRow }: {
  rows: ExampleCells[]; startIndex: number; onOpenRow?: (row: ExampleCells) => void
}) {
  const t = useT()
  return (
    <table className="w-full table-fixed border-collapse text-[11px]">
      {/* Сумма — 816px: столько остаётся от 940-пиксельного drawer после его
          собственных отступов, отступов секции и карточки причины. Шире — и
          правые колонки уезжают за край, а коды груза обрезаются на середине. */}
      <colgroup>
        <col className="w-[24px]" />
        <col className="w-[86px]" />
        <col className="w-[96px]" />
        <col className="w-[70px]" />
        <col className="w-[114px]" />
        <col className="w-[114px]" />
        <col className="w-[82px]" />
        <col className="w-[118px]" />
        <col className="w-[82px]" />
        <col className="w-[32px]" />
      </colgroup>
      <thead>
        <tr className="bg-[var(--bg-subtle)]">
          <th scope="col" className={`${TH} text-right`}>№</th>
          <th scope="col" className={TH}>{t('common.field.date')}</th>
          <th scope="col" className={TH}>{t('analytics.evi.shipmentsImpExp')}</th>
          <th scope="col" className={TH}>{t('analytics.evi.wagonContShort')}</th>
          <th scope="col" className={TH} title={t('analytics.evi.cargoImportHint')}>{t('analytics.caseDrawer.cargoImport')}</th>
          <th scope="col" className={TH} title={t('analytics.evi.cargoExportHint')}>{t('analytics.caseDrawer.cargoExport')}</th>
          <th scope="col" className={TH} title={t('analytics.evi.payerHint')}>
            {t('analytics.hist.payersImpExp')}
          </th>
          <th scope="col" className={TH}>{t('common.field.route')}</th>
          <th scope="col" className={`${TH} text-right`} title={t('analytics.evi.massHint')}>
            {t('analytics.hist.massImpExp')}
          </th>
          <th scope="col" className={`${TH} text-right`} title={t('analytics.evi.daysHint')}>
            {t('analytics.hist.daysCol')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          // Кликабельна только строка с доказанной идентичностью обеих ног.
          const open = onOpenRow && hasExactIdentity(r) ? onOpenRow : null
          return (
          <tr
            key={r.key}
            data-evidence-example={r.key}
            {...(open ? rowInteraction(r, open, t) : {})}
            className={`align-top hover:bg-[var(--bg-subtle)] ${open
              ? 'cursor-pointer focus:outline-none focus-visible:bg-[var(--bg-subtle)] ' +
                'focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[var(--accent-data)]'
              : ''}`}
          >
            <td className={`${TD} text-right font-mono tabular-nums text-[var(--text-tertiary)]`}>
              {startIndex + i + 1}
            </td>
            <td className={TD}>
              {/* Дата корпусного события. Дата импортной ноги не дублируется здесь —
                  интервал до неё стоит в колонке «{t('analytics.hist.daysCol')}». */}
              <Stacked main={r.date} mono />
            </td>
            <td className={TD}><ShipmentsCell r={r} /></td>
            <td className={TD}><UnitCell r={r} /></td>
            <td className={TD} data-leg="import">
              <CargoLegCell name={r.importCargo} etsng={r.importEtsng} gng={r.importGng} />
            </td>
            <td className={TD} data-leg="export">
              <CargoLegCell name={r.exportCargo} etsng={r.exportEtsng} gng={r.exportGng} />
            </td>
            <td className={TD}><LegPairCell imp={r.importPayer} exp={r.exportPayer} /></td>
            <td className={TD}><RouteCell route={r.route} stations={r.stations} /></td>
            <td className={TD}>
              {/* Массу каждой ноги печатаем как есть. Отсутствующая сторона — «—»:
                  ни 0, ни значение соседней ноги. Единица — в заголовке колонки. */}
              <LegPairCell
                imp={r.importWeight ? r.importWeight.replace(' т', '') : null}
                exp={r.exportWeight ? r.exportWeight.replace(' т', '') : null}
                end
                unit={t('analytics.hist.tonneShort')}
              />
            </td>
            <td className={`${TD} text-right font-mono tabular-nums text-[var(--text-primary)]`}>
              {r.days != null ? r.days : DASH}
            </td>
          </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/**
 * Ключ, по которому строка попала в набор, и роль на её стороне.
 *
 * Значение у всех строк одно — это ключ запроса. Читается колонка ради ВТОРОЙ строки:
 * «получатель» у импортной ноги и «отправитель» у экспортной — то самое, из-за чего один
 * участник законно стоит в строках обеих сторон.
 */
function ParticipantCell({ r }: { r: ExampleCells }) {
  if (!r.participant && !r.participantRole) return DASH
  return (
    <div
      className="flex min-w-0 flex-col"
      title={[r.participant, r.participantRole].filter(Boolean).join(' · ')}
    >
      {r.participant && (
        <span className="truncate font-mono tabular-nums text-[var(--text-primary)]">{r.participant}</span>
      )}
      {r.participantRole && <span className={`truncate ${MUTED}`}>{r.participantRole}</span>}
    </div>
  )
}

/**
 * Строка-нога (сценарии 6–7): одна ЗАПИСЬ КОРПУСА одной стороны, а не пара. Термин
 * «связка» к ней неприменим — см. ANOMALY_EVIDENCE_TERMINOLOGY_AUDIT.md.
 *
 * Колонка «Сторона» ОСТАВЛЕНА, а не переписана в «Экспорт»: набор действительно содержит
 * ноги обеих сторон — так определён признак, — и подмена подписи скрыла бы это, ничего не
 * исправив. Рядом с ней стоит участник со своей ролью: вместе они читаются как «это тот же
 * БИН, здесь он получатель», а не как «показаны чужие строки».
 *
 * Строка кликабельна ровно тогда, когда сервер прислал её `source_uid` — единственную
 * точную идентичность записи корпуса.
 */
function LegExamplesTable({ rows, startIndex, onOpenRow }: {
  rows: ExampleCells[]; startIndex: number; onOpenRow?: (row: ExampleCells) => void
}) {
  const t = useT()
  return (
    <table className="w-full table-fixed border-collapse text-[11px]">
      <colgroup>
        <col className="w-[28px]" />
        <col className="w-[80px]" />
        <col className="w-[58px]" />
        <col className="w-[80px]" />
        <col className="w-[78px]" />
        <col className="w-[104px]" />
        <col className="w-[132px]" />
        <col className="w-[92px]" />
        <col className="w-[122px]" />
        <col className="w-[72px]" />
      </colgroup>
      <thead>
        <tr className="bg-[var(--bg-subtle)]">
          <th scope="col" className={`${TH} text-right`}>№</th>
          <th scope="col" className={TH}>{t('common.field.date')}</th>
          <th scope="col" className={TH}>{t('analytics.evi.side')}</th>
          <th scope="col" className={TH}>{t('analytics.evi.shipment')}</th>
          <th scope="col" className={TH}>{t('analytics.evi.wagonContShort')}</th>
          {/* Колонка добавлена нами: ключ вхождения записи в показатель и роль
              участника на этой стороне. Ключа под подпись в словаре модуля нет. */}
          <th
            scope="col"
            className={TH}
            title="Ключ, по которому запись вошла в показатель, и роль участника на этой стороне"
          >
            Участник
          </th>
          <th scope="col" className={TH}>{t('common.field.cargo')}</th>
          <th scope="col" className={TH}>{t('analytics.evi.cargoCode')}</th>
          <th scope="col" className={TH}>{t('common.field.route')}</th>
          <th scope="col" className={`${TH} text-right`} title={t('analytics.evi.weightHint')}>{t('analytics.evi.weightShort')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const open = onOpenRow && hasExactLegIdentity(r) ? onOpenRow : null
          return (
            <tr
              key={r.key}
              data-evidence-example={r.key}
              {...(open ? rowInteraction(r, open, t) : {})}
              className={`align-top hover:bg-[var(--bg-subtle)] ${open
                ? 'cursor-pointer focus:outline-none focus-visible:bg-[var(--bg-subtle)] ' +
                  'focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[var(--accent-data)]'
                : ''}`}
            >
              <td className={`${TD} text-right font-mono tabular-nums text-[var(--text-tertiary)]`}>
                {startIndex + i + 1}
              </td>
              <td className={TD}><Stacked main={r.date} mono /></td>
              <td className={TD}>{r.sideLabel ?? DASH}</td>
              <td className={TD}><ShipmentsCell r={r} /></td>
              <td className={TD}><UnitCell r={r} /></td>
              <td className={TD} data-cell="participant"><ParticipantCell r={r} /></td>
              <td className={TD}><CargoCell name={r.cargo} /></td>
              <td className={TD}><CargoCodeCell etsng={r.etsng} gng={r.gng} /></td>
              <td className={TD}><RouteCell route={r.route} stations={r.stations} /></td>
              <td className={`${TD} text-right`}><Stacked main={r.weight} mono end /></td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/* ── the variants table ──────────────────────────────────────────────────────── */

/**
 * «Варианты комбинации кодов груза» / «Другие грузы на вывоз».
 *
 * A variant is a group with its OWN full count and a capped sample inside it, so it cannot
 * be flattened into the movements table without the counts losing their owner. Each group
 * gets a header line — cargo, its codes, and how many movements the group holds in full —
 * and the sample underneath in the same movements table.
 */
export function EvidenceVariantsTable({ groups, onOpenRow }: {
  groups: VariantGroup[]
  /** Строки внутри группы кликабельны ровно так же, как в плоской таблице. */
  onOpenRow?: (row: ExampleCells, group: VariantGroup) => void
}) {
  const f = useFormat()
  return (
    <div className="divide-y divide-[var(--border-subtle)]">
      {groups.map((g) => (
        <div key={g.key} data-evidence-variant={g.groupKey}>
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-2.5 py-1.5">
            {/* Первой строкой стоит ключ группировки, а не название груза: варианты
                различаются именно кодами, и у одного вагона несколько групп подряд
                могут нести одно и то же название. Название груза осталось второй
                строкой — как подпись к коду, а не как имя группы. */}
            <div className="min-w-0">
              <span
                data-variant-key-label={g.keyLabel ?? undefined}
                className="block truncate font-mono text-[11px] tabular-nums text-[var(--text-primary)]"
                title={g.keyLabel ?? g.groupKey}
              >
                {g.keyLabel ?? g.groupKey}
              </span>
              <span
                className="block truncate text-[10px] leading-tight text-[var(--text-tertiary)]"
                title={g.cargo ? `Наименование груза первой связки группы: ${g.cargo}` : undefined}
              >
                {g.cargo ?? '—'}
              </span>
            </div>
            <span className="shrink-0 rounded-[2px] bg-[var(--bg-subtle)] px-1.5 py-px font-mono text-[10px] tabular-nums text-[var(--text-secondary)]">
              {f.number(g.count)}
            </span>
          </div>
          {g.rows.length > 0 && (
            <div className="overflow-x-auto border-t border-[var(--border-subtle)]">
              <EvidenceExamplesTable
                rows={g.rows}
                onOpenRow={onOpenRow ? (row) => onOpenRow(row, g) : undefined}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
